// Single source of truth for the Israeli BBAN layout:
//   bank code (3) + branch code (3) + account number (13) = 19 digits.
// Splits a stored/assembled BBAN into its parts, zero-pads the parts and
// reassembles them — shared by the open_finance add-account form (DPM-545) and
// the beneficiary form so both use one parser instead of duplicating the logic.
export const BBAN_BANK_CODE_LENGTH = 3;
export const BBAN_BRANCH_CODE_LENGTH = 3;
export const BBAN_ACCOUNT_NUMBER_LENGTH = 13;
export const BBAN_LENGTH = BBAN_BANK_CODE_LENGTH + BBAN_BRANCH_CODE_LENGTH + BBAN_ACCOUNT_NUMBER_LENGTH;

// Digit-count validation for entered BBAN parts (before zero-padding): 1–3 / 1–3
// / 1–13 digits. Defined once here so both the open_finance add-account form and
// the beneficiary form validate against the same rule instead of duplicating it.
export const BBAN_BANK_CODE_REGEX = /^\d{1,3}$/;
export const BBAN_BRANCH_CODE_REGEX = /^\d{1,3}$/;
export const BBAN_ACCOUNT_NUMBER_REGEX = /^\d{1,13}$/;

export interface BbanParts {
  bankCode: string;
  branchCode: string;
  accountNumber: string;
}

export const splitBban = (assembled: string | null | undefined): BbanParts => {
  const value = assembled ?? '';
  const branchStart = BBAN_BANK_CODE_LENGTH;
  const accountStart = BBAN_BANK_CODE_LENGTH + BBAN_BRANCH_CODE_LENGTH;
  return {
    bankCode: value.slice(0, branchStart),
    branchCode: value.slice(branchStart, accountStart),
    accountNumber: value.slice(accountStart, accountStart + BBAN_ACCOUNT_NUMBER_LENGTH),
  };
};

// Right-align each entered part within its fixed width, filling the leading
// positions with zeros ('2' -> '002', '21' -> '021', '' -> '000'). This is both
// how the read-only preview renders (zeros as placeholders, never dashes) and
// how the value is normalised before it is sent to the backend.
export const padBbanParts = ({ bankCode, branchCode, accountNumber }: BbanParts): BbanParts => ({
  bankCode: bankCode.padStart(BBAN_BANK_CODE_LENGTH, '0'),
  branchCode: branchCode.padStart(BBAN_BRANCH_CODE_LENGTH, '0'),
  accountNumber: accountNumber.padStart(BBAN_ACCOUNT_NUMBER_LENGTH, '0'),
});

export const assembleBban = (parts: BbanParts): string => {
  const padded = padBbanParts(parts);
  return `${padded.bankCode}${padded.branchCode}${padded.accountNumber}`;
};
